/**
 * Export Utilities
 * Functions for exporting data to CSV format
 */

/**
 * Convert data array to CSV string
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions with field and headerName
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return ''

  // Filter out action columns and get exportable columns
  const exportColumns = columns.filter(col => col.field !== 'actions' && !col.noExport)

  // Create header row
  const headers = exportColumns.map(col => `"${col.headerName || col.field}"`)
  const headerRow = headers.join(',')

  // Create data rows
  const dataRows = data.map(row => {
    return exportColumns.map(col => {
      let value = row[col.field]

      // Handle null/undefined
      if (value === null || value === undefined) {
        value = ''
      }

      // Use export formatter if provided, otherwise use value directly
      if (col.exportValue) {
        value = col.exportValue(value, row)
      }

      // Convert to string and escape quotes
      value = String(value).replace(/"/g, '""')

      return `"${value}"`
    }).join(',')
  })

  return [headerRow, ...dataRows].join('\n')
}

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadCSV = (data, columns, filename = 'export') => {
  const csv = convertToCSV(data, columns)
  if (!csv) {
    console.warn('No data to export')
    return false
  }

  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  const timestamp = new Date().toISOString().split('T')[0]

  link.href = url
  link.download = `${filename}_${timestamp}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)

  return true
}

/**
 * Format currency value for export
 * @param {number} value - Currency value
 * @param {string} currency - Currency code (default: UGX)
 * @returns {string} Formatted currency string
 */
export const formatCurrencyForExport = (value, currency = 'UGX') => {
  if (!value && value !== 0) return ''
  return `${currency} ${Number(value).toLocaleString()}`
}

/**
 * Format date for export
 * @param {string|Date} value - Date value
 * @returns {string} Formatted date string
 */
export const formatDateForExport = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

/**
 * Format datetime for export
 * @param {string|Date} value - DateTime value
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeForExport = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
